import type { StationState } from "@station-keeping/sim";
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { useGameUi } from "../game/store";
import { TUTORIAL_STEPS, useTutorial, type TutorialTarget } from "../game/tutorial";
import { t } from "../i18n";
import { PixelText } from "./pixel";
import { Key, Led } from "./primitives";

/** Position de l'élément visé, suivie à chaque image (panneau qui s'ouvre, défilement, rotation). */
function useTargetRect(target: TutorialTarget | undefined): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!target) {
      setRect(null);
      return;
    }
    let frame = 0;
    let last = "";
    let scrolled = false;
    const track = () => {
      const element = document.querySelector<HTMLElement>(`[data-tutorial="${target}"]`);
      if (element && !scrolled) {
        // Une rangée d'un panneau peut être hors de vue : on l'y amène une fois.
        element.scrollIntoView({ block: "nearest" });
        scrolled = true;
      }
      const box = element?.getBoundingClientRect();
      const key = box ? `${Math.round(box.x)},${Math.round(box.y)},${Math.round(box.width)},${Math.round(box.height)}` : "";
      if (key !== last) {
        last = key;
        setRect(box && box.width > 0 ? box : null);
      }
      frame = requestAnimationFrame(track);
    };
    track();
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return rect;
}

/** Quatre équerres de pixels autour de l'élément visé, qui clignotent par paliers. */
function Brackets({ rect }: { rect: DOMRect }) {
  const pad = "calc(var(--px) * 3)";
  const corner = "absolute size-[calc(var(--px)*6)] border-accent";
  const width = "calc(var(--px) * 2)";
  return (
    <div
      aria-hidden="true"
      className="blink pointer-events-none fixed z-30"
      style={{
        left: `calc(${rect.left}px - ${pad})`,
        top: `calc(${rect.top}px - ${pad})`,
        width: `calc(${rect.width}px + ${pad} * 2)`,
        height: `calc(${rect.height}px + ${pad} * 2)`,
      }}
    >
      <span className={`${corner} left-0 top-0`} style={{ borderLeftWidth: width, borderTopWidth: width }} />
      <span className={`${corner} right-0 top-0`} style={{ borderRightWidth: width, borderTopWidth: width }} />
      <span className={`${corner} bottom-0 left-0`} style={{ borderLeftWidth: width, borderBottomWidth: width }} />
      <span className={`${corner} bottom-0 right-0`} style={{ borderRightWidth: width, borderBottomWidth: width }} />
    </div>
  );
}

const MARGIN = 16;
const GAP = 18;

/**
 * Où poser le bandeau sur grand écran : à côté de l'élément encadré (sous lui s'il est dans le fronton, à
 * sa droite sinon), pour que la consigne et sa cible se lisent ensemble ; sans cible, en bas à droite.
 */
function bannerPlacement(rect: DOMRect | null, size: { width: number; height: number }): CSSProperties {
  if (!rect) return { right: MARGIN, bottom: MARGIN };
  const viewWidth = window.innerWidth;
  const viewHeight = window.innerHeight;
  // Dans le fronton (score, orbite) : le bandeau se pose dessous ; ailleurs (pupitre, panneau) : à droite,
  // sans jamais recouvrir le fronton.
  const marquee = document.querySelector('[data-tutorial="hud"]')?.getBoundingClientRect();
  const inMarquee = marquee !== undefined && rect.bottom <= marquee.bottom + 1;
  const minTop = marquee ? marquee.bottom + 8 : MARGIN;
  const clampLeft = (left: number) => Math.max(MARGIN, Math.min(viewWidth - size.width - MARGIN, left));
  const clampTop = (top: number) => Math.max(minTop, Math.min(viewHeight - size.height - MARGIN, top));
  if (!inMarquee && rect.right + GAP + size.width <= viewWidth - MARGIN) {
    return { left: rect.right + GAP, top: clampTop(rect.top + rect.height / 2 - size.height / 2) };
  }
  if (rect.bottom + GAP + size.height <= viewHeight - MARGIN) {
    return { left: clampLeft(rect.left + rect.width / 2 - size.width / 2), top: rect.bottom + GAP };
  }
  return { right: MARGIN, bottom: MARGIN };
}

/**
 * Le tutoriel de l'arrivée : une consigne par bandeau, posé près de l'élément concerné, encadré. Certaines
 * étapes se font (ouvrir le menu, choisir, poser) : elles passent d'elles-mêmes, sans touche « Suivant ».
 */
export function Tutorial({ state, desktop, bottom }: { state: StationState; desktop: boolean; bottom: number }) {
  const active = useTutorial((tutorial) => tutorial.active);
  const step = useTutorial((tutorial) => tutorial.step);
  const entry = useTutorial((tutorial) => tutorial.entry);
  const next = useTutorial((tutorial) => tutorial.next);
  const finish = useTutorial((tutorial) => tutorial.finish);
  const panel = useGameUi((ui) => ui.panel);
  const buildType = useGameUi((ui) => ui.buildType);
  const current = active ? TUTORIAL_STEPS[step] : undefined;
  const rect = useTargetRect(current?.target);
  const banner = useRef<HTMLElement>(null);
  const [size, setSize] = useState({ width: 416, height: 180 });
  useLayoutEffect(() => {
    const element = banner.current;
    if (!element) return;
    if (element.offsetWidth !== size.width || element.offsetHeight !== size.height) {
      setSize({ width: element.offsetWidth, height: element.offsetHeight });
    }
  });

  useEffect(() => {
    if (current?.done?.(state, useGameUi.getState(), entry)) next(state);
  }, [current, state, panel, buildType, entry, next]);

  if (!current) return null;
  const last = step === TUTORIAL_STEPS.length - 1;
  const toDo = Boolean(current.done);
  return (
    <>
      {rect ? <Brackets rect={rect} /> : null}
      <div
        className={`pointer-events-none fixed z-30 flex ${desktop ? "w-[26rem]" : "inset-x-0 justify-center px-3"}`}
        style={desktop ? bannerPlacement(rect, size) : { bottom }}
      >
        <section
          ref={banner}
          className="bezel wipe-in pointer-events-auto w-full max-w-[26rem]"
          aria-live="polite"
          aria-label={t("tutorial.label")}
        >
          <div className="px-2 pb-2 pt-1">
            <div className="dotted flex items-center gap-2 pb-2 pt-1">
              <Led on blink color="var(--color-accent)" />
              <h2 className="flex-1 text-ink">
                <PixelText text={t(`tutorial.${current.id}.title`)} />
              </h2>
              <span className="text-dim">
                <PixelText text={`${step + 1}/${TUTORIAL_STEPS.length}`} label={t("tutorial.progress", { step: step + 1, total: TUTORIAL_STEPS.length })} />
              </span>
            </div>
            <p className="mt-2 text-[0.9375rem] leading-snug text-ink">{t(`tutorial.${current.id}.body`)}</p>
            {toDo ? <p className="mt-2 text-[0.875rem] text-accent">{t("tutorial.yourTurn")}</p> : null}
            <div className="mt-3 flex items-center justify-between gap-2">
              <Key tone="quiet" size="sm" onClick={finish}>
                {t("tutorial.skip")}
              </Key>
              {toDo ? null : (
                <Key tone="action" size="sm" onClick={() => next(state)}>
                  {last ? t("tutorial.finish") : step === 0 ? t("tutorial.start") : t("tutorial.next")}
                </Key>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
